// Comparing app versions ("1.0.15"), for "who still needs to update".

/** "1.0.15" → [1, 0, 15]; null for anything that isn't dotted numbers (up to 4 parts). */
export function parseVersion(v: unknown): number[] | null {
  if (typeof v !== 'string') return null;
  const s = v.trim();
  if (!/^[0-9]+(\.[0-9]+){0,3}$/.test(s)) return null;
  return s.split('.').map(Number);
}

/** What an admin may announce: at least "major.minor", at most four parts. */
export function cleanReleaseVersion(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const s = v.trim();
  return /^[0-9]+(\.[0-9]+){1,3}$/.test(s) ? s : null;
}

/** -1, 0 or 1: how version a compares with b. Missing parts count as 0 ("1.0" = "1.0.0"). */
export function compareVersions(a: number[], b: number[]): -1 | 0 | 1 {
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const x = a[i] ?? 0;
    const y = b[i] ?? 0;
    if (x !== y) return x < y ? -1 : 1;
  }
  return 0;
}

/**
 * Does this device still need the announced version? True when it runs an older one, and
 * also when it does not report a version (phones on builds from before the app sent it).
 */
export function needsUpdate(deviceVersion: string | null | undefined, announced: string): boolean {
  const latest = parseVersion(announced);
  if (!latest) return false;
  const current = parseVersion(deviceVersion);
  if (!current) return true;
  return compareVersions(current, latest) < 0;
}

/** The newest version any phone reports, and how many phones are on it; null when none reports one. */
export function latestVersion(versions: Array<string | null | undefined>): { version: string; devices: number } | null {
  let best: { parts: number[]; version: string } | null = null;
  for (const v of versions) {
    const parts = parseVersion(v);
    if (!parts) continue;
    if (!best || compareVersions(parts, best.parts) > 0) best = { parts, version: parts.join('.') };
  }
  if (!best) return null;
  const top = best;
  const devices = versions.filter((v) => { const p = parseVersion(v); return p && compareVersions(p, top.parts) === 0; }).length;
  return { version: top.version, devices };
}

export const RELEASE_TITLE = 'A new version of Sekretly is ready';
export const RELEASE_BODY = 'Update the app to get the latest improvements.';

/** Only the two stores' own pages may be opened from the push. */
export function isStoreUrl(url: unknown): url is string {
  if (typeof url !== 'string' || url.length > 500) return false;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && (u.hostname === 'apps.apple.com' || u.hostname === 'play.google.com');
  } catch {
    return false;
  }
}
